import { useMemo, useRef } from 'react'
import type { ComponentProps } from 'react'
import { InView } from 'react-intersection-observer'
import { PLP_COLLECTION_COMPONENTS_EXPERIMENT_ID } from 'src/components/common/StatsigExposure/StatsigExposure'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { KitlookCollectionCMSWrapper } from 'src/components/KitlookWidget/KitlookCollectionCMSWrapper'
import { SphereContent } from 'src/components/restructure/product/sections/SphereContent'
import { useMobile } from 'src/hooks/useMobile'
import { Statsig } from 'src/sdk/statsig'
import { useSearch } from '@faststore/sdk'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude/dispatchAmplitudeEvent'

import { InspiringShowcase } from '../InspiringShowcase'
import { InlineHorizontalBanner } from '../InlineHorizontalBanner'
import { InlineVerticalBanner } from '../InlineVerticalBanner'
import { InlineFullBanner } from './ProductGalleryv3'
import { filterComponentsForPosition } from './utils/filterComponentsForPosition'

interface CollectionCMSData {
  enableShowcase: boolean
  backgroundImage: ComponentProps<typeof InspiringShowcase>['backgroundImage']
  title: ComponentProps<typeof InspiringShowcase>['title']
  showOnMobile: boolean
  showOnDesktop: boolean
  products: ComponentProps<typeof InspiringShowcase>['products']
  sphereId: string
  linkUrl: ComponentProps<typeof InlineHorizontalBanner>['linkUrl']
  bannerConfig: {
    imageMobile: ComponentProps<
      typeof InlineHorizontalBanner
    >['bannerUrlMobile']
    imageDesktop: ComponentProps<
      typeof InlineHorizontalBanner
    >['bannerUrlDesktop']
    imageDescription: ComponentProps<
      typeof InlineHorizontalBanner
    >['imageDescription']
    bannerName?: string
    linkURL?: string
    title?: string
    showButton?: boolean
    buttonText?: string
  }
  isActive?: boolean
  name?: string
  imageDesktop?: string
  imageMobile?: string
  displayPage?: number
  displayLine?: number
  elementPosition: {
    mobilePosition: number
    desktopPosition: number
    pagination: number
  }
}

interface KitlookCollectionCMSComponentData {
  title: string
  buttonText?: string
  image: string
  productId: string
  elementPosition: {
    pagination: number
    mobilePosition: number
    desktopPosition: number
  }
}

export type CollectionCMSComponent = {
  id: string
  name: string
  data: CollectionCMSData
}

type SphereContentItem = { sphereId: string; sphereHtml: string | null }

type CollectionComponentListItem = {
  component: JSX.Element
  positionMobile: number
  positionDesktop: number
  pageToShow: number
}

interface UseComponentListParams {
  collectionCMSData?: CollectionCMSComponent[]
  productsLength: number
  page: number
  sphereContents?: SphereContentItem[]
}

export function useComponentList({
  collectionCMSData,
  productsLength,
  page,
  sphereContents,
}: UseComponentListParams): {
  componentList: Array<CollectionComponentListItem | null> | null
  isMobile: boolean
} {
  const isCollectionComponentsEnabled = Statsig.useStatsigFeatureGateValue(
    PLP_COLLECTION_COMPONENTS_EXPERIMENT_ID
  )

  const { isMobile } = useMobile()
  const { itemsPerPage } = useSearch()
  const desktopGridColumns = 4

  const { pageData: globalPageData } = useGlobalContext()
  const resolvedPageData = globalPageData as ComponentProps<
    typeof SphereContent
  >['pageData']

  const viewedSphereEventIdsRef = useRef<Set<string>>(new Set())

  const componentList = useMemo(() => {
    const buildPositionConfig = (
      component: CollectionCMSComponent
    ): Omit<CollectionComponentListItem, 'component'> | null => {
      const position = component.data?.elementPosition

      if (
        position?.mobilePosition === undefined ||
        position?.desktopPosition === undefined ||
        position?.pagination === undefined
      ) {
        const displayLine = component.data?.displayLine

        if (displayLine === undefined || displayLine === null) {
          return null
        }

        const normalizedLine = Math.max(1, Math.trunc(Number(displayLine)))
        const pageToShow = Number(component.data?.displayPage ?? 1)

        return {
          positionMobile: (normalizedLine - 1) * 2,
          positionDesktop: (normalizedLine - 1) * desktopGridColumns,
          pageToShow: Number.isFinite(pageToShow) ? pageToShow : 1,
        }
      }

      return {
        positionMobile: position.mobilePosition,
        positionDesktop: position.desktopPosition,
        pageToShow: position.pagination,
      }
    }

    if (
      !collectionCMSData ||
      !isCollectionComponentsEnabled ||
      (productsLength < itemsPerPage && (page === 1 || page === 0))
    ) {
      return null
    }

    const filteredComponents = filterComponentsForPosition(
      collectionCMSData,
      isMobile
    )

    return filteredComponents.map((component) => {
      if (
        component.name === '[Coleção] Vitrine Inspiracional' &&
        component.data?.enableShowcase
      ) {
        const positionConfig = buildPositionConfig(component)

        if (!positionConfig) return null

        return {
          component: (
            <li key={component.id} className="col-span-2 md:col-span-4">
              <InspiringShowcase
                backgroundImage={component.data.backgroundImage}
                title={component.data.title}
                showOnMobile={component.data.showOnMobile}
                showOnDesktop={component.data.showOnDesktop}
                products={component.data.products}
                enableShowcase={component.data.enableShowcase}
              />
            </li>
          ),
          ...positionConfig,
        }
      }

      if (
        component.name === '[Coleção] Conteúdo Sphere' &&
        sphereContents?.length &&
        component.data?.enableShowcase &&
        ((isMobile && component.data?.showOnMobile) ||
          (!isMobile && component.data?.showOnDesktop))
      ) {
        const sphereContent = sphereContents.find(
          (item) => item.sphereId === component.data.sphereId
        )

        const positionConfig = buildPositionConfig(component)

        if (!sphereContent?.sphereHtml || !positionConfig) return null

        return {
          component: (
            <li
              key={component.id}
              className="pb-12 md:pb-16 md:pt-[28px] col-span-2 md:col-span-4"
            >
              <InView
                as="section"
                triggerOnce
                onChange={(inView) => {
                  const alreadyDispatched = viewedSphereEventIdsRef.current.has(
                    component.id
                  )

                  if (inView && !alreadyDispatched) {
                    viewedSphereEventIdsRef.current.add(component.id)

                    dispatchAmplitudeEvent({
                      eventName: 'Promo Feature Viewed',
                      eventData: {
                        type: 'Contextual',
                        'promotional element name': 'Sphere',
                      },
                    })
                  }
                }}
              >
                <SphereContent
                  html={sphereContent.sphereHtml}
                  pageData={resolvedPageData}
                />
              </InView>
            </li>
          ),
          ...positionConfig,
        }
      }

      if (
        component.name === '[Coleção 2.0] Banner Full' &&
        component.data?.isActive
      ) {
        const positionConfig = buildPositionConfig(component)

        if (
          !positionConfig ||
          !component.data?.imageMobile ||
          !component.data?.imageDesktop
        ) {
          return null
        }

        return {
          component: (
            <li
              key={component.id}
              className="mobile:col-span-2 small:col-span-4 h-full"
            >
              <InlineFullBanner
                bannerUrlMobile={component.data.imageMobile}
                bannerUrlDesktop={component.data.imageDesktop}
                imageDescription={component.data.name ?? 'Banner Full'}
                linkUrl={component.data.linkUrl}
                promotionalName={component.data.name}
              />
            </li>
          ),
          ...positionConfig,
        }
      }

      if (
        (component.name === '[Coleção] Banner inline horizontal' ||
          component.name === '[Search] Banner inline horizontal') &&
        component.data?.enableShowcase &&
        ((isMobile && component.data?.showOnMobile) ||
          (!isMobile && component.data?.showOnDesktop))
      ) {
        const positionConfig = buildPositionConfig(component)

        if (!positionConfig) return null

        return {
          component: (
            <li
              key={component.id}
              className="col-span-2 md:col-span-4 h-full product-inline-horizontal-banner-slot"
            >
              <InlineHorizontalBanner
                bannerUrlMobile={component.data?.bannerConfig?.imageMobile}
                bannerUrlDesktop={component.data?.bannerConfig?.imageDesktop}
                imageDescription={
                  component.data?.bannerConfig?.imageDescription
                }
                bannerName={component.data?.bannerConfig?.bannerName}
                linkUrl={
                  component.data?.linkUrl ??
                  component.data?.bannerConfig?.linkURL ??
                  ''
                }
              />
            </li>
          ),
          ...positionConfig,
        }
      }

      if (
        (component.name === '[Coleção] Banner inline Vertical' ||
          component.name === '[Search] Banner inline Vertical') &&
        component.data?.enableShowcase &&
        ((isMobile && component.data?.showOnMobile) ||
          (!isMobile && component.data?.showOnDesktop))
      ) {
        const positionConfig = buildPositionConfig(component)

        if (!positionConfig) return null

        return {
          component: (
            <li
              key={component.id}
              className="col-span-1 h-full product-inline-vertical-banner-slot"
            >
              <InlineVerticalBanner
                bannerUrlMobile={
                  component.data?.bannerConfig?.imageMobile ?? ''
                }
                bannerUrlDesktop={
                  component.data?.bannerConfig?.imageDesktop ?? ''
                }
                imageDescription={
                  component.data?.bannerConfig?.imageDescription ?? ''
                }
                bannerName={component.data?.bannerConfig?.bannerName}
                title={component.data?.bannerConfig?.title}
                showButton={component.data?.bannerConfig?.showButton}
                buttonText={component.data?.bannerConfig?.buttonText}
                linkUrl={
                  component.data?.linkUrl ??
                  component.data?.bannerConfig?.linkURL ??
                  ''
                }
              />
            </li>
          ),
          ...positionConfig,
        }
      }

      if (component.name === '[Coleção 2.0] Widget Kitlook') {
        const kitlookData =
          component.data as unknown as KitlookCollectionCMSComponentData

        const positionConfig = buildPositionConfig(component)

        if (!positionConfig) return null

        const resolvedProductId = kitlookData.productId

        return {
          component: (
            <KitlookCollectionCMSWrapper
              key={component.id}
              title={kitlookData.title}
              buttonText={kitlookData.buttonText}
              image={kitlookData.image}
              productId={resolvedProductId}
            />
          ),
          ...positionConfig,
        }
      }

      return null
    })
  }, [
    collectionCMSData,
    isCollectionComponentsEnabled,
    desktopGridColumns,
    productsLength,
    itemsPerPage,
    page,
    sphereContents,
    isMobile,
    resolvedPageData,
  ])

  return {
    componentList,
    isMobile,
  }
}
